import { MouseEvent, UIEvent } from 'react';
import { Table as AntTable, TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import clsx from 'clsx';
import styles from './CustomTable.module.scss';

export type DataTableTheme = 'dark' | 'light';

// Opts antd into its split header/body markup; the height itself comes from
// the flex layout `fillHeight` applies.
const SPLIT_LAYOUT_SENTINEL = 1;
const REACH_END_THRESHOLD_PX = 200;
const INTERACTIVE_CELL_CONTENT = '.ant-checkbox, .ant-dropdown-trigger, button, a, input';

export interface CustomTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  columns: ColumnsType<T>;
  theme?: DataTableTheme;
  /** Fill a definite-height parent and make the table body the only scroller. */
  fillHeight?: boolean;
  /** Never fires in `virtual` mode -- that body is transform-driven, not scrolled. */
  onReachEnd?: () => void;
  reachEndThresholdPx?: number;
  onRowClick?: (record: T, index?: number) => void;
  onRowDoubleClick?: (record: T, index?: number) => void;
}

const CustomTable = <T extends object>({
  columns,
  theme = 'dark',
  fillHeight = false,
  onReachEnd,
  reachEndThresholdPx = REACH_END_THRESHOLD_PX,
  onRowClick,
  onRowDoubleClick,
  className,
  scroll,
  onRow,
  ...rest
}: CustomTableProps<T>) => {
  const isRowClickable = !!onRowClick || !!onRowDoubleClick;

  // Scroll events don't bubble, so the capture phase is what sees the table
  // body scrolling from out here.
  const handleScrollCapture = (event: UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!target.classList?.contains('ant-table-body')) return;
    const { scrollHeight, scrollTop, clientHeight } = target;
    if (scrollHeight - scrollTop - clientHeight < reachEndThresholdPx) {
      onReachEnd?.();
    }
  };

  // Composed, not replaced: a caller's own onRow handlers still run.
  const buildRowProps: TableProps<T>['onRow'] = (record, index) => {
    const callerProps = onRow?.(record, index) ?? {};

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      callerProps.onClick?.(event);
      if ((event.target as HTMLElement).closest(INTERACTIVE_CELL_CONTENT)) return;
      onRowClick?.(record, index);
    };

    const handleDoubleClick = (event: MouseEvent<HTMLElement>) => {
      callerProps.onDoubleClick?.(event);
      if ((event.target as HTMLElement).closest(INTERACTIVE_CELL_CONTENT)) return;
      onRowDoubleClick?.(record, index);
    };

    return { ...callerProps, onClick: handleClick, onDoubleClick: handleDoubleClick };
  };

  return (
    <div
      className={clsx(styles.root, styles[theme], fillHeight && styles.fillHeight, className)}
      onScrollCapture={onReachEnd ? handleScrollCapture : undefined}
    >
      <AntTable<T>
        {...rest}
        columns={columns}
        scroll={fillHeight ? { ...scroll, y: scroll?.y ?? SPLIT_LAYOUT_SENTINEL } : scroll}
        onRow={isRowClickable ? buildRowProps : onRow}
      />
    </div>
  );
};

export default CustomTable;
